import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import Image from "next/image";

import FetchError from "@/components/ui/fetch-error";
import Loading from "@/components/ui/loading";

import DataAccess from "./data-access";
import DataCollection from "./data-collection";
import DataLinkages from "./data-linkages";
import HarmonizedDatasets from "./harmonized-datasets";
import Sample from "./sample";
import SamplingFrame from "./sampling-frame";

import api from "@/lib/api";

type OverviewModalProps = {
  cohort_name: string;
  logo_url: string | null;
  download_url: string;
  description: string;
  cohort_profile_link: string;
  cohort_profile_text: string;
  period_chart_url: string;
  sample_response_graph_url: string;
  extra_fields: {
    sampling_frame_desc: string;
    sampling_frame_types: string;
    data_collection_desc: string;
    data_collection_types: string;
    sample_desc: string;
    data_linkage_desc: string;
    data_linkage_types: string;
    data_access_desc: string;
    data_access_sources: string;
    harmonized_datasets_intro: string;
    harmonized_datasets: string;
    cohort_profile_text: string;
    cohort_profile_link: string;
    secondary_cohort_profile_link: string;
    secondary_cohort_profile_text: string;
  };
}[];

export default function OverviewModal({ studyId, cohort_name }: { studyId: number; cohort_name: string | undefined }) {
  const [dataArrayIndex, setDataArrayIndex] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<string>("SamplingFrame");

  const {
    data: modalData = [],
    error,
    isPending,
  } = useQuery<OverviewModalProps>({
    queryKey: ["hcap-srv-overview-modal", studyId],
    queryFn: () => api.get<OverviewModalProps>(`/srv/study/${studyId}/?site_name=hcap`),
    enabled: !!studyId,
  });

  useEffect(() => {
    if (!cohort_name || modalData.length === 0) return;

    const index = modalData.findIndex((item) => item.cohort_name.toLowerCase() === cohort_name.toLowerCase());

    if (index !== -1) {
      setDataArrayIndex(index);
    }
  }, [cohort_name, modalData]);

  if (isPending) return <Loading />;
  if (error) return <FetchError message={error.message} />;

  const res_data = modalData[dataArrayIndex];

  const renderComponent = () => {
    if (!res_data.extra_fields) return null;

    switch (activeTab) {
      case "SamplingFrame":
        return (
          <SamplingFrame
            sampling_frame_desc={res_data.extra_fields.sampling_frame_desc}
            sampling_frame_types={res_data.extra_fields.sampling_frame_types}
          />
        );
      case "DataCollection":
        return (
          <DataCollection
            data_collection_desc={res_data.extra_fields.data_collection_desc}
            data_collection_types={res_data.extra_fields.data_collection_types}
            period_chart={res_data.period_chart_url}
          />
        );
      case "Sample":
        return (
          <Sample
            sample_desc={res_data.extra_fields.sample_desc}
            sample_response_graph={res_data.sample_response_graph_url}
          />
        );
      case "DataLinkages":
        return (
          <DataLinkages
            data_linkage_desc={res_data.extra_fields.data_linkage_desc}
            data_linkage_types={res_data.extra_fields.data_linkage_types}
          />
        );
      case "DataAccess":
        return (
          <DataAccess
            data_access_desc={res_data.extra_fields.data_access_desc}
            data_access_sources={res_data.extra_fields.data_access_sources}
          />
        );
      case "HarmonizedDatasets":
        return (
          <HarmonizedDatasets
            harmonized_datasets_intro={res_data.extra_fields.harmonized_datasets_intro}
            harmonized_datasets={res_data.extra_fields.harmonized_datasets}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div>
      {modalData.length > 1 && (
        <div className="row my-3">
          <div className="col mb-4">
            <div className="input-group mb">
              <span
                className="input-group-text text-uppercase g2-text-blue fw-semibold"
                style={{ backgroundColor: "#e8ebf3" }}
              >
                <i className="bi bi-menu-button-wide me-2"></i>Cohort Menu
              </span>
              <select
                id="cohort-select"
                className="form-select"
                value={dataArrayIndex}
                onChange={(event) => setDataArrayIndex(Number(event.target.value))}
              >
                {modalData.map((item, index) => (
                  <option key={index} value={index}>
                    {item.cohort_name}
                  </option>
                ))}
              </select>
            </div>
            <h6 className="g2-text-blue">* Select country/cohort of your interest</h6>
          </div>
        </div>
      )}

      {res_data.logo_url && (
        <div className="text-center">
          <Image src={res_data.logo_url} alt="logo" className="mb-3" width={250} height={50} />
        </div>
      )}

      <h6 className="d-block fw-semibold text-center">{res_data.cohort_name}</h6>
      <p className="text-center">
        <a href={res_data.download_url} className="text-decoration-none g2-link-blue" target="_blank">
          <i className="bi bi-globe2 me-2"></i>
          {res_data.download_url}
        </a>
      </p>

      <div className="alert" style={{ backgroundColor: "#e8ebf3" }}>
        <p>{res_data.description}</p>

        {res_data?.extra_fields?.cohort_profile_text?.trim() && (
          <span className="g2-link-blue">
            <small>
              <span className="fw-bold">Cohort Profile: </span>
              <a className="g2-link-blue" href={res_data.extra_fields.cohort_profile_link} target="_blank">
                {res_data.extra_fields.cohort_profile_text}
              </a>
            </small>
          </span>
        )}

        {res_data?.extra_fields?.secondary_cohort_profile_text?.trim() && (
          <>
            <br />
            <span className="g2-link-blue">
              <small>
                <span className="fw-bold">Secondary Cohort Profile: </span>
                <a className="g2-link-blue" href={res_data.extra_fields.secondary_cohort_profile_link} target="_blank">
                  {res_data.extra_fields.secondary_cohort_profile_text}
                </a>
              </small>
            </span>
          </>
        )}
      </div>

      <ul className="nav nav-pills justify-content-center mt-4 mb-3">
        {["SamplingFrame", "DataCollection", "Sample", "DataLinkages", "DataAccess", "HarmonizedDatasets"].map(
          (tab) => (
            <li className="nav-item" key={tab}>
              <a
                href="#"
                className={`nav-link ${activeTab === tab ? "active" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab(tab);
                }}
              >
                {tab.replace(/([A-Z])/g, " $1").trim()}
              </a>
            </li>
          ),
        )}
      </ul>

      {renderComponent()}
    </div>
  );
}
